import { Alert, Button, Divider, message, Modal, Space, Typography } from "antd";
import { ApiService } from "api/api.service";
import {
  TbAlertCircle,
  TbAlertTriangle,
  TbAutomation,
  TbCalendarRepeat,
  TbCircleCheck,
  TbCreditCard,
  TbLock,
} from "react-icons/tb";
import { queryClient } from "utils/queryClient";

const { Paragraph, Text, Title } = Typography;

export const StartPaymentAutomationModal = ({
  open,
  onClose,
  appkey,
  applicationId,
  readiness,
  ledgerIdx = 0,
}: {
  open: boolean;
  onClose: () => void;
  appkey: string;
  applicationId: string;
  ledgerIdx?: number;
  readiness?: any;
}) => {
  const startPaymentAutomation = ApiService.ledger.useStartPaymentAutomation();

  const handleStart = () => {
    startPaymentAutomation.mutate(
      { appkey, idx: ledgerIdx },
      {
        onSuccess: () => {
          onClose();

          queryClient.invalidateQueries({
            queryKey: ["application/payment-table", applicationId],
          });

          queryClient.invalidateQueries({
            queryKey: ["application", appkey],
          });

          queryClient.invalidateQueries({
            queryKey: ["application", "wallet/balance", applicationId],
          });

          message.success("Initial payment processed and future weekly payments have been scheduled.");
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleStart}
      okText="Start Automation"
      width={640}
      centered
      okButtonProps={{
        danger: true,
        size: "large",
        loading: startPaymentAutomation.isPending,
      }}
      cancelButtonProps={{
        disabled: startPaymentAutomation.isPending,
        size: "large",
      }}
      title={
        <Space align="center">
          <TbAutomation size={22} />
          <span>Start Weekly Payment Automation</span>
        </Space>
      }
    >
      <Alert
        type="warning"
        showIcon
        icon={<TbAlertTriangle />}
        style={{ marginBottom: 20 }}
        message="This action is important and partially irreversible."
        description="Please review all ledger details and payment methods before continuing."
      />

      <Title level={5}>What will happen?</Title>

      <Space direction="vertical" size={18} style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
        >
          <TbCreditCard size={20} style={{ marginTop: 2 }} />

          <div>
            <Text strong>Initial payment processing</Text>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              {`The system will attempt to charge from the saved payment methods for the payment amount of week ${ledgerIdx}.`}
            </Paragraph>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
        >
          <TbCalendarRepeat size={20} style={{ marginTop: 2 }} />

          <div>
            <Text strong>Weekly recurring automation</Text>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              After the successful payment of this week, all future ledger payments will be scheduled automatically on a
              weekly/monthly basis.
            </Paragraph>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}
        >
          <TbLock size={20} style={{ marginTop: 2 }} />

          <div>
            <Text strong>Ledger and application locking</Text>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Once automation begins, most application details, ledger rows, and related payment configurations will no
              longer be editable or removable.
            </Paragraph>
          </div>
        </div>
      </Space>

      <Divider />

      {readiness && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>Pre-flight checklist (recommended to review first)</Text>

          <div
            style={{
              marginTop: 8,
              padding: "12px 14px",
              background: "var(--ant-color-fill-quaternary)",
              borderRadius: 8,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {[
              {
                label: "All loans linked to creditor model",
                ok: !readiness.unlinkedLoans,
              },
              {
                label: "All loans linked to partner creditors (if needed)",
                ok: !readiness.unlinkedPartnerCreditors,
              },
              {
                label: "PandaDoc signatures complete",
                ok: readiness.pandaDocComplete,
              },
              {
                label: "Payment methods added to application",
                ok: readiness.hasPaymentMethods,
              },
            ].map(({ label, ok }) => (
              <Space key={label} size={8}>
                {ok ? (
                  <TbCircleCheck size={15} style={{ color: "var(--ant-color-success)" }} />
                ) : (
                  <TbAlertCircle size={15} style={{ color: "var(--ant-color-text-tertiary)" }} />
                )}

                <Text
                  style={{
                    fontSize: 13,
                    color: ok ? undefined : "var(--ant-color-text-tertiary)",
                  }}
                >
                  {label}
                </Text>
              </Space>
            ))}
          </div>
        </div>
      )}

      <Paragraph style={{ marginBottom: 0 }}>
        Please ensure all application details, payment methods, and ledger information are finalized before proceeding
        to ensure seamless automated payment processing.
      </Paragraph>
    </Modal>
  );
};
